import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import type { ReactElement } from "react";
import { useCallback } from "react";

import { getDataFromResponse } from "~/api/axiosUtils.ts";
import { login, logout } from "~/features/auth/api/auth/authApiDispatchers.ts";
import { AUTHENTICATED_USER_QUERY_KEY } from "~/features/auth/api/auth/authQueries.ts";
import type { AuthenticatedUser } from "~/features/auth/api/auth/authTypes.ts";
import AuthContext from "~/features/auth/contexts/AuthContext";
import type {
    HasAnyPermissionParamsType,
    HasAnyRolesParamsType,
} from "~/features/auth/contexts/AuthContext/authContextTypes.ts";
import { deleteAccessToken, setAccessToken } from "~/services/tokenService.ts";

type AuthContextProviderProps = {
    user: AuthenticatedUser | null;
    children: React.ReactNode;
};

const AuthContextProvider = ({ user, children }: AuthContextProviderProps): ReactElement => {
    const router = useRouter();
    const queryClient = useQueryClient();

    const signIn = useCallback(async (email: string, password: string) => {
        try {
            const loginResponse = await login({ email, password });
            const responseData = getDataFromResponse(loginResponse);
            const accessToken = responseData.accessToken;

            setAccessToken(accessToken);
        } catch (error) {
            console.log({ error });
            throw new Error("Login failed");
        }
    }, []);

    const signOut = useCallback(async () => {
        try {
            await logout();
        } catch (error) {
            console.log(error);
        }

        deleteAccessToken();
    }, []);

    const refetchUser = useCallback(async () => {
        await queryClient.invalidateQueries({ queryKey: AUTHENTICATED_USER_QUERY_KEY });
        await queryClient.refetchQueries({ queryKey: AUTHENTICATED_USER_QUERY_KEY });
        await router.invalidate();
    }, [router, queryClient]);

    const hasAnyPermission = useCallback(
        (permissionsToCheck: HasAnyPermissionParamsType) => {
            if (!user) return false;

            if (!permissionsToCheck) return true;
            return true;
            /* let permissionsToCheckArray: PermissionName[];

            if (typeof permissionsToCheck === "string") {
                permissionsToCheckArray = [permissionsToCheck];
            } else {
                permissionsToCheckArray = permissionsToCheck as PermissionName[];
            }

            return (
                permissionsToCheckArray.filter((elem) => user.permissions.find((permission) => permission === elem))
                    .length > 0
            );*/
        },
        [user],
    );

    const hasAnyRole = useCallback(
        (rolesToCheck: HasAnyRolesParamsType) => {
            if (!user) return false;

            if (!rolesToCheck) return true;
            return true;
            /*let rolesToCheckArray: RoleName[];

            if (typeof rolesToCheck === "string") {
                rolesToCheckArray = [rolesToCheck];
            } else {
                rolesToCheckArray = rolesToCheck as RoleName[];
            }
            return true;
            return rolesToCheckArray.some((elem) => user.roles.some((role) => role === elem));*/
        },
        [user],
    );

    return (
        <AuthContext.Provider
            value={{
                user: user ?? undefined,
                signIn,
                signOut,
                hasAnyPermission,
                hasAnyRole,
                refetchUser,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContextProvider;
